import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, TrendingUp, Pencil, Trash2, Archive, ArchiveRestore, AlertTriangle, Users, RefreshCw } from "lucide-react";
import { fmtBRL, fmtDate } from "@/lib/api";

/**
 * Card individual de contrato.
 * Callbacks: onEdit, onReajustar, onArquivar, onRestaurar, onExcluir.
 * `isMultiplo` e `isVencendoOuVencido` são funções derivadas do parent
 * (dependem da lista completa para detectar múltiplas ocupações).
 */
export function ContratoCard({ c, isMultiplo, isVencendoOuVencido, isReajustePendente, onEdit, onReajustar, onArquivar, onRestaurar, onExcluir }) {
  const isArquivado = c.status === "ARQUIVADO";
  return (
    <Card data-testid={`contrato-card-${c.cnpj}`} className={`p-6 rounded-sm border-border ${isArquivado ? "opacity-70 bg-muted/20" : ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[280px] min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <FileText size={16} />
            <span className="font-heading font-bold text-xl break-anywhere">{c.razao_social}</span>
            <Badge variant="outline" className="uppercase tracking-widest text-[10px]">{c.tipo_pessoa}</Badge>
            <Badge variant={c.status === "ATIVO" ? "default" : "outline"}
              className={`uppercase tracking-widest text-[10px] ${isArquivado ? "border-muted-foreground/40 text-muted-foreground" : ""}`}>
              {c.status}
            </Badge>
            {isMultiplo && (
              <Badge data-testid={`badge-multiplo-${c.cnpj}`} variant="outline" className="uppercase tracking-widest text-[10px] border-info/60 text-info bg-info/5">
                <Users size={10} className="mr-1" /> Múltiplas unidades
              </Badge>
            )}
            {isVencendoOuVencido && (
              <Badge data-testid={`badge-vencendo-${c.cnpj}`} variant="outline" className="uppercase tracking-widest text-[10px] border-warning/60 text-warning bg-warning/5">
                <AlertTriangle size={10} className="mr-1" />
                {c.dias_para_termino > 0 ? `Vence em ${c.dias_para_termino}d` : "Vigência vencida"}
              </Badge>
            )}
            {isReajustePendente && (
              <Badge data-testid={`badge-reajuste-${c.cnpj}`} variant="outline" className="uppercase tracking-widest text-[10px] border-info/60 text-info bg-info/5">
                <RefreshCw size={10} className="mr-1" />
                Reajuste pendente · {c.meses_desde_reajuste}m
              </Badge>
            )}
          </div>
          <div className="text-xs font-mono text-muted-foreground">
            {c.cnpj} · {c.telefone} · Resp: {c.responsavel}
          </div>
          <div className="flex flex-wrap gap-1 mt-3">
            {c.unidades?.map((u) => (
              <Badge key={u.id} variant="outline" className="text-[10px]">{u.numero_completo}</Badge>
            ))}
          </div>
          {(c.fiador?.nome || c.caucao?.valor) && (
            <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap gap-4 text-xs">
              {c.fiador?.nome && (
                <div>
                  <span className="label-tag block mb-1">Fiador</span>
                  <span className="font-mono">{c.fiador.nome}{c.fiador.documento ? ` · ${c.fiador.documento}` : ""}</span>
                </div>
              )}
              {c.caucao?.valor && (
                <div>
                  <span className="label-tag block mb-1">Caução</span>
                  <span className="font-mono">{fmtBRL(c.caucao.valor)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="text-right space-y-1">
          <div className="label-tag">Aluguel base</div>
          <div className="stat-num text-3xl">{fmtBRL(c.valor_aluguel)}</div>
          <div className="text-[10px] text-muted-foreground font-mono">
            Multa {c.multa_atraso_percent ?? 2}% · Mora {c.mora_diaria_percent ?? 0.033}%/dia
          </div>
          {(c.taxa_agua_mensal ?? 0) > 0 && (
            <div className="text-[10px] text-info font-mono">+ Água {fmtBRL(c.taxa_agua_mensal)}/mês</div>
          )}
          {(c.taxa_rateio_fixa ?? 0) > 0 && (
            <div className="text-[10px] text-warning font-mono">Rateio contratual fixo: {fmtBRL(c.taxa_rateio_fixa)}/mês</div>
          )}
          <div className="text-xs text-muted-foreground">{fmtDate(c.data_inicio)} → {fmtDate(c.data_fim)}</div>
          {c.dias_para_termino != null && (
            <div className={`text-xs font-mono ${c.dias_para_termino < 90 ? "text-warning" : "text-muted-foreground"}`}>
              {c.dias_para_termino > 0 ? `${c.dias_para_termino} dias restantes` : "Vencido"}
            </div>
          )}
          <Button data-testid={`btn-reajuste-${c.cnpj}`} size="sm" variant="outline" className="rounded-sm mt-2"
            onClick={() => onReajustar(c)} disabled={isArquivado}>
            <TrendingUp size={12} className="mr-1" /> Reajustar
          </Button>
          <div className="flex gap-1 mt-2 justify-end flex-wrap">
            <Button data-testid={`btn-editar-${c.cnpj}`} size="sm" variant="outline" className="rounded-sm"
              onClick={() => onEdit(c)} title="Editar contrato" disabled={isArquivado}>
              <Pencil size={12} className="mr-1" /> Editar
            </Button>
            {isArquivado ? (
              <Button data-testid={`btn-restaurar-${c.cnpj}`} size="sm" variant="outline"
                className="rounded-sm border-info/40 text-info hover:bg-info/5"
                onClick={() => onRestaurar(c)} title="Restaurar contrato">
                <ArchiveRestore size={12} className="mr-1" /> Restaurar
              </Button>
            ) : (
              <Button data-testid={`btn-arquivar-${c.cnpj}`} size="sm" variant="outline"
                className="rounded-sm border-warning/40 text-warning hover:bg-warning/5"
                onClick={() => onArquivar(c)} title="Arquivar contrato (encerra e libera unidades preservando histórico)">
                <Archive size={12} className="mr-1" /> Arquivar
              </Button>
            )}
            <Button data-testid={`btn-excluir-${c.cnpj}`} size="sm" variant="outline"
              className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/5"
              onClick={() => onExcluir(c)} title="Excluir permanentemente">
              <Trash2 size={12} className="mr-1" /> Excluir
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
